import classNames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import compact from "lodash/compact";
import debounce from "lodash/debounce";
import { useAppDispatch, useAppStoreContext } from "PFApp/app_context";
import { CvExportsButton } from "PFApp/components/cv_exports_button";
import { TemplateKey } from "PFApp/constants/templates";
import SearchKeywordSliders from "PFApp/search/parts/search_keyword_sliders.jsx";
import { Inline } from "PFComponents/containers";
import { TooltipIcon } from "PFComponents/tooltip";
import { buildFilterV2 } from "PFCore/helpers/filters/build_filter_v2.js";
import { getAttribute } from "PFCore/helpers/get_attribute";
import { useDebounce } from "PFCore/helpers/use_debounce";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringify";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useMyAccountInfo } from "PFCore/hooks/queries/account/use_my_account_info";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { SEARCH_ACTIONS, SEARCH_ASYNC_ACTIONS } from "PFReducers/search_reducer/search_reducer";
import { PermissionRule } from "PFTypes";
import { ApiFilterGroupV2, KnownFiltersV2 } from "PFTypes/filters_v2/index.js";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { HeaderToolbox } from "../components/header_toolbox";
import { getAPIFilters } from "../use_filtered_collection";
import SearchFilters from "./parts/search_filters";
import ResultsActivities from "./results/results_activities";
import ResultsAll from "./results/results_all";
import ResultsMatches from "./results/results_matches";
import ResultsProfiles from "./results/results_profiles";
import ChoosenKeywords from "./search_box/choosen_keywords";
import { SearchBox } from "./search_box/search_box";
import css from "./search_page.module.scss";
import SearchTabs from "./search_tabs";
import SmartSuggestions from "./smart_suggestions/smart_suggestions";
import {
  activitiesListTab,
  activitiesTab,
  allTab,
  allTabs,
  KEYWORD_FILTER_KEY,
  matchesTab,
  profilesTab
} from "./tabs_constants";

const Title = ({ tab, count }) => {
  const { t } = useTranslation("search");
  if (count === undefined || count === null) {
    return "";
  }

  return (
    <Inline alignItems="center">
      <span>
        {tab === activitiesTab ? t("countActivitiesFound", { count }) : t("countProfilesFound", { count })}
      </span>
      <TooltipIcon content={t("resultsDependOnPerms")} />
    </Inline>
  );
};

Title.propTypes = {
  tab: PropTypes.string,
  count: PropTypes.number
};

const SearchPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const stringify = useDeterministicStringify();
  const { data: currentProfile } = useCurrentProfile();
  const { data: myAccountInfo } = useMyAccountInfo();

  const dispatch = useAppDispatch();
  const {
    search: {
      profiles,
      matches,
      triggerCounter,
      term,
      tab,
      searchTaskId,
      searchTaskFetchCounter,

      choosenKeywords,

      activitiesRequestFilters,
      profilesRequestFilters,
      matchesRequestFilters,

      activitiesRequestOrder,
      profilesRequestOrder,
      matchesRequestOrder,

      activitiesPending,
      profilesPending,
      matchesPending,

      profilesMeta,
      matchesMeta,
      availableTemplates,

      activitiesPagination,
      profilesPage,
      matchesPage,

      allActivities,
      allActivitiesMeta,

      matchTypes,
      filterTypes,
      searchTask,
      deletedKeywords,
      collectionKind,
      prevMatchesFetch
    }
  } = useAppStoreContext();

  const activitiesRequestAPIFilters = useMemo(
    () =>
      availableTemplates.reduce((acc, template) => {
        acc[template.key] = getAPIFilters(activitiesRequestFilters[template.key]) || {};
        return acc;
      }, {}),
    [availableTemplates, activitiesRequestFilters]
  );

  const profilesRequestAPIFilters = useMemo(
    () => getAPIFilters(profilesRequestFilters) || {},
    [profilesRequestFilters]
  );

  const matchesRequestAPIFilters = useMemo(
    () => getAPIFilters(matchesRequestFilters) || {},
    [matchesRequestFilters]
  );

  const isPermittedTo = useIsPermittedTo(currentProfile);

  const trimmedTerm = term.trim(); // avoid triggering effects on no-changes (use in the deps)

  useEffect(() => {
    if (!isPermittedTo(PermissionRule.ProfileList)) {
      navigate("/");
    }
  }, [isPermittedTo, navigate]);

  useEffect(() => {
    const availableTemplates = myAccountInfo.templates.filter(
      (template) => !template.hidden && template.read_permitted
    );
    dispatch({ type: SEARCH_ACTIONS.SET_AVAILABLE_TEMPLATES, payload: { availableTemplates } });
    dispatch({ type: SEARCH_ACTIONS.SET_ACTIVITIES_PAGINATION, payload: { availableTemplates } });
    dispatch({ type: SEARCH_ACTIONS.SEARCH_SET_TYPES, payload: { myAccountInfo } });

    return () => dispatch({ type: SEARCH_ACTIONS.SEARCH_RESET_STATE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keywords
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    useDebounce(() => {
      if (!matchTypes.length && !filterTypes.length) {
        return;
      }
      const type = SEARCH_ASYNC_ACTIONS.SEARCH_FETCH_SUGGESTED_KEYWORDS;
      dispatch({
        type,
        payload: { choosenKeywords, term, matchTypes, filterTypes }
      });
    }, 500),
    [
      trimmedTerm,
      tab,
      choosenKeywords.map((keyword) => `${getAttribute(keyword, "globalId")},${keyword.id}`).join(":"),
      matchTypes,
      filterTypes
    ]
  );

  const debouncedGoToPage1Dispatch = useCallback(
    () =>
      debounce((payload) => {
        dispatch(SEARCH_ACTIONS.SEARCH_MERGE_STATE, payload);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // activities
  const activitiesParamsHash = stringify([activitiesRequestAPIFilters, activitiesRequestOrder]);

  const fetchAllActivitiesDebounced = useDebounce(
    () =>
      dispatch({
        type: SEARCH_ASYNC_ACTIONS.SEARCH_FETCH_ALL_ACTIVITIES,
        payload: {
          term,
          availableTemplates,
          activitiesRequestOrder,
          activitiesRequestFilters: activitiesRequestAPIFilters
        }
      }),
    500
  );

  // update actvities when page is changed
  useEffect(() => {
    const page = activitiesPagination && activitiesPagination[tab];
    if (page > 1) {
      dispatch({
        type: SEARCH_ACTIONS.SEARCH_MORE_ACTIVITIES,
        payload: {
          allActivities,
          tab,
          activitiesPagination,
          availableTemplates,
          term,
          activitiesRequestFilters: activitiesRequestAPIFilters,
          activitiesRequestOrder
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesPagination]);

  // update activities when filters are changed
  useEffect(() => {
    dispatch({
      type: SEARCH_ACTIONS.SEARCH_MORE_ACTIVITIES,
      payload: {
        allActivities,
        tab,
        availableTemplates,
        term,
        activitiesRequestFilters: activitiesRequestAPIFilters,
        activitiesRequestOrder
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesParamsHash]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(debouncedGoToPage1Dispatch({ activitiesPagination: {} }), [activitiesParamsHash]);

  useEffect(() => {
    dispatch({ type: SEARCH_ACTIONS.SEARCH_ABORT_ACTIVITIES });
    fetchAllActivitiesDebounced();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedTerm, triggerCounter]);

  // profiles
  const profilesParamsHash = stringify([profilesRequestAPIFilters, profilesRequestOrder]);
  const fetchProfilesDebounced = useDebounce(
    () =>
      dispatch({
        type: SEARCH_ASYNC_ACTIONS.SEARCH_FETCH_PROFILES,
        payload: {
          term,
          profilesRequestOrder,
          profilesPage,
          profilesRequestFilters: profilesRequestAPIFilters
        }
      }),
    500
  );

  useEffect(() => {
    debouncedGoToPage1Dispatch({ profilesPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilesParamsHash]);

  useEffect(() => {
    fetchProfilesDebounced();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedTerm, profilesPage, profilesParamsHash, triggerCounter]);

  // matches
  const matchesParamsHash = stringify([matchesRequestAPIFilters, matchesRequestOrder]);
  const choosenKeywordsHash = choosenKeywords
    .map(({ id, importance }) => `${id}:${importance}`)
    .sort()
    .join(",");
  const taskTemplateId = myAccountInfo.templates?.find(({ key }) => key === TemplateKey.Task)?.id;
  const fetchMatchesDebounced = useDebounce(
    () =>
      dispatch({
        type: SEARCH_ASYNC_ACTIONS.SEARCH_TRIGGER_MATCHES_FETCH,
        payload: {
          term,
          searchTask,
          searchTaskId,
          choosenKeywords,
          deletedKeywords,
          matchesPage,
          matchesRequestOrder,
          matchesRequestFilters: matchesRequestAPIFilters,
          collectionKind,
          prevMatchesFetch,
          searchTaskFetchCounter,
          taskTemplateId
        }
      }),
    500
  );

  useEffect(() => {
    debouncedGoToPage1Dispatch({ matchesPage: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosenKeywordsHash, trimmedTerm, matchesParamsHash]);
  useEffect(() => {
    dispatch({ type: SEARCH_ACTIONS.SEARCH_ABORT_MATCHES });
    fetchMatchesDebounced();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedTerm, choosenKeywordsHash, matchesPage, matchesParamsHash, triggerCounter]);

  // update url
  const updateUrl = useCallback(() => {
    if (searchTaskFetchCounter === 0) {
      return;
    }
    const encodedTerm = window.encodeURI(term || "").replace(/\//g, "%2f");
    const safeTab = allTabs.includes(tab) ? tab : "all";
    // If there is no choosenKeywords then the searchTask would not be reloadable.
    // You cannot save a task without custom_fields - we dont even atempt (see fetchMatches)
    // We still keep the id in memory so we can reuse the task but if user reloads the page
    // the id is lost.
    //
    //const safeId = choosenKeywords.length > 0 && searchTaskId ? searchTaskId : "new";
    const safeId = searchTaskId || "new";

    if (safeTab === matchesTab && !searchTaskId && matchesPending) {
      // when you load the task for the first time and no choosenKeywords then
      // we don't want to update the url (it'd update to "new" and then back to
      // the proper id after the fetch
      return;
    }

    if (window.encodeURI(location.pathname) !== `/search/${safeId}/${safeTab}/${encodedTerm}`) {
      navigate(`/search/${safeId}/${safeTab}/${encodedTerm}`);
    }
  }, [searchTaskFetchCounter, navigate, location.pathname, tab, searchTaskId, matchesPending, term]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(useDebounce(updateUrl, 10), [
    tab,
    trimmedTerm,
    searchTaskId,
    choosenKeywords,
    matchesPending,
    searchTaskFetchCounter
  ]);

  const filtersAllowed = !(tab === matchesTab && matches.length === 0);

  const matchesMetaWithKeywordFilters = useMemo(() => {
    if (!matchesMeta.filtersV2) {
      return matchesMeta;
    }

    const metaDraft = cloneDeep(matchesMeta);
    // we want to render Keyword Sliders inside filters panel- that's how it was done in the old version
    // Keywords should be removed from filters panel in new design of global search page
    // or keyword filters should be better integrated with normal filters
    metaDraft.filtersV2.mainFilters[KEYWORD_FILTER_KEY] = buildFilterV2({
      name: KEYWORD_FILTER_KEY,
      group: ApiFilterGroupV2.Fields,
      collection: "",
      ordinal: 1,
      InputComponent: SearchKeywordSliders,
      inputStyle: { fullWidth: true }
    });

    return metaDraft;
  }, [matchesMeta]);

  const exportButtonProps = {
    text: t("exportText"),
    icon: undefined,
    kind: "primary"
  };

  return (
    <div className={css.root}>
      <SmartSuggestions />
      <HeaderToolbox
        className={css.headerToolbox}
        items={compact([
          tab === matchesTab && (
            <CvExportsButton
              profileIds={(matches || []).map(({ profile }) => profile.id)}
              activityId={searchTask?.id}
              {...exportButtonProps}
            />
          ),
          tab === profilesTab && (
            <CvExportsButton profileIds={(profiles || []).map(({ id }) => id)} {...exportButtonProps} />
          )
        ])}
        showSegment
      />
      <div className={classNames(css.wrap, { [css.wrapWithNoTerm]: !filtersAllowed })}>
        <div className={css.content}>
          <SearchBox />
          <ChoosenKeywords />

          {tab === matchesTab && (
            <SearchFilters
              omitInSidePanel={[KnownFiltersV2.Skills]}
              pending={matchesPending}
              meta={matchesMetaWithKeywordFilters}
              applyRequestFilters={(payload) => {
                dispatch({ type: SEARCH_ACTIONS.SEARCH_MERGE_MATCHES_REQUEST_FILTERS, payload });
              }}
              applyOrder={(payload) => {
                dispatch({ type: SEARCH_ACTIONS.SEARCH_MERGE_MATCHES_REQUEST_ORDER, payload });
              }}
            />
          )}

          {activitiesListTab.includes(tab) && (
            <SearchFilters
              key={tab}
              omitCompletely={[KnownFiltersV2.TemplateKey]} // template key is managed by tab
              pending={activitiesPending}
              meta={allActivitiesMeta?.[tab]}
              applyRequestFilters={(payload) => {
                dispatch({
                  type: SEARCH_ACTIONS.SEARCH_MERGE_ACTIVITIES_REQUEST_FILTERS,
                  payload
                });
              }}
              applyOrder={(payload) => {
                dispatch({
                  type: SEARCH_ACTIONS.SEARCH_MERGE_ACTIVITIES_REQUEST_ORDER,
                  payload
                });
              }}
            />
          )}

          {tab === profilesTab && (
            <SearchFilters
              pending={profilesPending}
              meta={profilesMeta}
              applyRequestFilters={(payload) => {
                dispatch({ type: SEARCH_ACTIONS.SEARCH_MERGE_PROFILES_REQUEST_FILTERS, payload });
              }}
              applyOrder={(payload) => {
                dispatch({ type: SEARCH_ACTIONS.SEARCH_MERGE_PROFILES_REQUEST_ORDER, payload });
              }}
            />
          )}

          <div className={css.header}>
            <h1>
              <Title
                tab={tab}
                count={
                  {
                    [matchesTab]: matchesMeta?.total,
                    [profilesTab]: profilesMeta?.total
                  }[tab]
                }
              />
            </h1>
          </div>

          <SearchTabs />
          {tab === allTab && <ResultsAll />}
          {activitiesListTab.includes(tab) && <ResultsActivities />}
          {tab === profilesTab && <ResultsProfiles />}
          {tab === matchesTab && <ResultsMatches />}
        </div>
      </div>
    </div>
  );
};

SearchPage.propTypes = {};

export default SearchPage;
